import { Api } from "../Api";
import {
  getEffectiveOrganizationName,
  clearOrganizationCache as clearOrgServiceCache,
} from "./organizationService";
import { config } from "../runtimeConfig";

var BACKEND_URL = config.BACKEND_URL;
if (BACKEND_URL === undefined) {
  BACKEND_URL = "";
}

export const getBackendUrl = (relativeUrl) => {
  return `${BACKEND_URL}${relativeUrl}`;
};

// ============================================================================
// ORGANIZATION HELPER FUNCTIONS (for analysis view selection only)
// ============================================================================

// Simple organization fetcher for analysis view selection
let organizationCache = null;

export const getOrganizationName = async () => {
  // First check if there's an organization override (for Ethosphere users)
  const effectiveOrgName = getEffectiveOrganizationName();
  if (effectiveOrgName) {
    return effectiveOrgName;
  }

  // Return cached result if available
  if (organizationCache) {
    return organizationCache;
  }

  try {
    const response = await Api.get("/user/info");
    organizationCache = response.data.organization.name;
    return organizationCache;
  } catch (error) {
    console.error("Error fetching organization:", error);
    return null;
  }
};

// Clear organization cache (useful for logout)
export const clearOrganizationCache = () => {
  organizationCache = null;
  clearOrgServiceCache(); // Also clear the organization service cache
};

// ============================================================================
// CURRENT WORKFLOW FUNCTIONS
// ============================================================================

export const getS3AudioUrl = async (conversation_id, state) => {
  try {
    const response = await Api.get(`/conversations/${conversation_id}/audio-url`);
    return response.data.url;
  } catch (error) {
    console.error("Error fetching audio URL for conversation", conversation_id, error);
    return null;
  }
};

export const getSignedConversationAudioUrl = async (conversation_id, state) => {
  // Use the same logic as getS3AudioUrl for consistency
  return getS3AudioUrl(conversation_id, state);
};

export const getSignatureUrl = async (objectId) => {
  try {
    let response = undefined;

    // Check if objectId is not just undefined but also not null and not empty string
    if (objectId !== undefined && objectId !== null && objectId !== "") {
      // /signatures/{signature_uuid}/url
      response = await Api.get(`/signatures/${objectId}/audio`);
    } else {
      // Fallback to user's signature URL
      response = await Api.get("/users/me/signature-url");
    }

    return response.data.signed_url;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching signature URL:", error);
    throw error;
  }
};

export const getConversationList = async () => {
  try {
    const response = await Api.get("/conversations");
    return response.data;
  } catch (error) {
    console.error("Error fetching conversation list:", error);
    throw error;
  }
};

export const createUpdateSignature = async (
  updatedSignature,
  isUpdate = false
) => {
  try {
    let response;
    if (isUpdate) {
      response = await Api.put(
        `/signatures/${updatedSignature.id}`,
        updatedSignature
      );
    } else {
      response = await Api.post("/signatures", updatedSignature);
    }
    return response.data;
  } catch (error) {
    console.error("Error saving signature metadata:", error);
    throw error;
  }
};

export const createConversation = async (conversation_uuid, filePath) => {
  try {
    // STEP 1: Create AudioSnippet first (mimicking 5-minute chunk)
    const audioSnippetResponse = await Api.post("/audio_snippets", {
      start_timestamp: new Date().toISOString(),
      end_timestamp: new Date(-1).toISOString(),
      unfiltered_audio_url: filePath,
    });

    const audioSnippetId = audioSnippetResponse.data.id;
    console.log("AudioSnippet created successfully. ID:", audioSnippetId);

    // STEP 2: Create Conversation
    const conversationResponse = await Api.post("/conversations", {
      source: "TestAudioConversation",
    });

    const conversationId = conversationResponse.data.id;
    console.log("Conversation created successfully. ID:", conversationId);

    // STEP 3: Create AudioSnippetRef with array structure
    await Api.post("/audio_snippet_refs", {
      audio_snippet_id: audioSnippetId,
      conversation_id: conversationId,
      start_time: [0], // Start at beginning of snippet
      end_time: [300], // Assume 5-minute snippet (300 seconds)
      duration: [300], // Full snippet duration
    });

    console.log(
      "AudioSnippetRef created, linking AudioSnippet",
      audioSnippetId,
      "to Conversation",
      conversationId
    );

    // Return just conversationId for backward compatibility
    return conversationId;
  } catch (error) {
    console.error("Error creating conversation:", error);
    throw error;
  }
};

export const reprocessConversation = async (conversation, onProgress) => {
  const s3_url = await getS3AudioUrl(conversation.id, "original");
  const transcribeURL = "http://0.0.0.0:8000/t/transcribe";
  const tagURL = "http://0.0.0.0:8000/t/tag";
  const analyzeURL = "http://0.0.0.0:8000/t/analyze";

  // Get the user's organization for analysis
  const organizationName = await getOrganizationName();
  const clientName = organizationName ? organizationName.toLowerCase() : "demo";

  console.log("s3_url", s3_url);
  console.log("Using client organization:", clientName);
  try {
    // Call transcribe API
    onProgress?.({ step: "transcribing", progress: 0.4 });
    const transcribeResponse = await fetch(transcribeURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audio_path: s3_url }),
    });
    // check for errors
    if (!transcribeResponse.ok) {
      throw new Error(
        `HTTP error on transcribe! status: ${transcribeResponse.status}`
      );
    }

    const transcribeData = await transcribeResponse.json();

    // Check if we got a successful response
    if (!transcribeData) {
      throw new Error("Transcribe response is empty");
    }

    // Call tag API with transcription
    onProgress?.({ step: "diarizing", progress: 0.5 });
    const tagResponse = await fetch(tagURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transcript: transcribeData.transcription,
      }),
    });
    // check for errors
    if (!tagResponse.ok) {
      throw new Error(`HTTP error on tag! status: ${tagResponse.status}`);
    }

    const tagData = await tagResponse.json();
    // check if tagData is empty
    if (!tagData) {
      throw new Error("Tag response is empty");
    }

    // Call analyze API with tagged transcript
    onProgress?.({ step: "analyzing", progress: 0.7 });
    const analyzeResponse = await fetch(analyzeURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transcript: tagData.result,
        client: clientName,
      }),
    });
    // check for errors
    if (!analyzeResponse.ok) {
      throw new Error(
        `HTTP error on analyze! status: ${analyzeResponse.status}`
      );
    }

    const analysisResponse = await analyzeResponse.json();
    // check if analysisResponse is empty
    if (!analysisResponse) {
      throw new Error("Analysis response is empty");
    }
    console.log("analysisResponse", analysisResponse);

    // Store results as ConversationDetailItems
    onProgress?.({ step: "saving", progress: 0.9 });

    // Store raw transcript as ConversationDetailItem
    await Api.post("/conversation_detail_items", {
      conversation_id: conversation.id,
      type: "RAW_TRANSCRIPT",
      description: "Raw transcript data (reprocessed)",
      data: transcribeData,
    });

    // Store filtered transcript as ConversationDetailItem
    await Api.post("/conversation_detail_items", {
      conversation_id: conversation.id,
      type: "FILTERED_TRANSCRIPT",
      description: "Filtered audio transcript (reprocessed)",
      data: tagData,
    });

    // Store analysis as ConversationDetailItem
    await Api.post("/conversation_detail_items", {
      conversation_id: conversation.id,
      type: "ANALYSIS",
      description: "Analysis results (reprocessed)",
      data: analysisResponse.analysis,
    });

    onProgress?.({ step: "completed", progress: 1.0 });

    // Return the conversation with updated structure
    return {
      ...conversation,
      raw_transcript_data: transcribeData,
      filtered_transcript_data: tagData,
      analysis: analysisResponse.analysis,
    };
  } catch (error) {
    console.error("Error in reprocessConversation:", error);
    throw error;
  }
};

export const createDemoConversation = async (
  conversation_uuid,
  description,
  s3_url,
  onProgress
) => {
  const transcribeURL = "http://0.0.0.0:8000/t/transcribe";
  const tagURL = "http://0.0.0.0:8000/t/tag";
  const analyzeURL = "http://0.0.0.0:8000/t/analyze";

  // Get the user's organization for analysis
  const organizationName = await getOrganizationName();
  const clientName = organizationName ? organizationName.toLowerCase() : "demo";

  console.log("Using client organization for demo conversation:", clientName);

  try {
    typeof onProgress === "function" &&
      onProgress({ step: "creating conversation", progress: 0.1 });

    // STEP 1: Create AudioSnippet first (mimicking 5-minute chunk)
    const audioSnippetResponse = await Api.post("/audio_snippets", {
      start_timestamp: new Date().toISOString(),
      end_timestamp: new Date(-1).toISOString(),
      unfiltered_audio_url: s3_url,
    });

    const audioSnippetId = audioSnippetResponse.data.id;
    console.log("AudioSnippet created successfully. ID:", audioSnippetId);

    // STEP 2: Create Conversation
    const conversationResponse = await Api.post("/conversations", {
      source: "TestAudioConversation",
    });

    const conversationId = conversationResponse.data.id;
    console.log("Conversation created successfully. ID:", conversationId);

    // STEP 3: Create AudioSnippetRef with array structure
    await Api.post("/audio_snippet_refs", {
      audio_snippet_id: audioSnippetId,
      conversation_id: conversationId,
      start_time: [0], // Start at beginning of snippet
      end_time: [300], // Assume 5-minute snippet (300 seconds)
      duration: [300], // Full snippet duration
    });

    console.log(
      "AudioSnippetRef created, linking AudioSnippet",
      audioSnippetId,
      "to Conversation",
      conversationId
    );

    typeof onProgress === "function" &&
      onProgress({ step: "transcribing", progress: 0.4 });

    // Call transcribe API
    const transcribeResponse = await fetch(transcribeURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ audio_path: s3_url }),
    });
    const transcribeData = await transcribeResponse.json();

    // Store raw transcript as soon as it's ready
    await Api.post("/conversation_detail_items", {
      conversation_id: conversationId,
      type: "RAW_TRANSCRIPT",
      description: "Raw transcript data",
      data: transcribeData,
    });

    // Call tag API with transcription
    typeof onProgress === "function" &&
      onProgress({ step: "diarizing", progress: 0.6 });

    const tagResponse = await fetch(tagURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transcript: transcribeData.transcription,
      }),
    });
    const tagData = await tagResponse.json();

    // Store filtered transcript as soon as it's ready
    await Api.post("/conversation_detail_items", {
      conversation_id: conversationId,
      type: "FILTERED_TRANSCRIPT",
      description: "Filtered audio transcript",
      data: tagData,
    });

    // Call analyze API with tagged transcript
    typeof onProgress === "function" &&
      onProgress({ step: "analyzing", progress: 0.8 });

    const analyzeResponse = await fetch(analyzeURL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        transcript: tagData.result,
        client: clientName,
      }),
    });

    const analysisResponse = await analyzeResponse.json();
    console.log("analysisResponse", analysisResponse);

    // Store analysis as soon as it's ready
    await Api.post("/conversation_detail_items", {
      conversation_id: conversationId,
      type: "ANALYSIS",
      description: "Analysis results",
      data: analysisResponse.analysis,
    });

    typeof onProgress === "function" &&
      onProgress({ step: "completed", progress: 1.0 });

    // Return the conversation that was created
    return conversationResponse.data;
  } catch (error) {
    console.error("Error in createDemoConversation:", error);
    throw error;
  }
};

export const loadConversation = async (conversation_id) => {
  try {
    // Use the direct endpoint for conversation details
    const detailsUrl = `/conversations/${conversation_id}/details`;
    console.log("Loading conversation details from:", detailsUrl);

    const response = await Api.get(detailsUrl);

    if (!response.data) {
      throw new Error("No conversation details found");
    }

    const conversationDetails = response.data;
    console.log("Loaded conversation details:", conversationDetails);

    // Transform the organized details into the expected conversation format
    const conversation = {
      id: conversationDetails.conversation_id || conversation_id,
      uuid: conversation_id, // Use conversation_id as uuid fallback
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Add raw transcript data if available
    if (conversationDetails.raw_transcript) {
      conversation.raw_transcript_data =
        conversationDetails.raw_transcript.data;
      conversation.raw_transcript =
        conversationDetails.raw_transcript.data?.transcription || null;
    }

    // Add filtered transcript data if available
    if (conversationDetails.filtered_transcript) {
      conversation.filtered_transcript_data =
        conversationDetails.filtered_transcript.data;
      conversation.filtered_transcript =
        conversationDetails.filtered_transcript.data?.result || null;
    }

    // Add analysis data if available
    if (conversationDetails.analysis) {
      conversation.analysis = conversationDetails.analysis.data;
    }

    // Add any other detail types that might exist
    Object.keys(conversationDetails).forEach((key) => {
      if (
        ![
          "conversation_id",
          "raw_transcript",
          "filtered_transcript",
          "analysis",
        ].includes(key)
      ) {
        // Add other detail types with their original key names
        conversation[key] = conversationDetails[key].data;
      }
    });

    console.log("Transformed conversation:", conversation);
    return conversation;
  } catch (error) {
    console.error("Error loading conversation from API:", error);

    // Fallback to old file-based approach if API fails
    console.log("Falling back to file-based conversation loading...");
    return loadConversationFromFile(conversation_id);
  }
};

// Keep the old file-based loading as a fallback function
const loadConversationFromFile = async (conversation_id) => {
  try {
    // Check if we're in folder-based mode (new workflow)
    const audioFolder = process.env.REACT_APP_AUDIO_FOLDER;
    let dataUrl;

    if (audioFolder) {
      // New folder-based workflow - use API endpoint
      dataUrl = `http://localhost:3001/serve-rttm/dummy.json`; // filename is handled by server
      console.log("Loading conversation data from API endpoint:", dataUrl);
    } else {
      // Old workflow - use command-line provided RTTM path or fallback to default
      const rttmPath = process.env.REACT_APP_RTTM_PATH;
      console.log("Environment RTTM path:", rttmPath);
      console.log(
        "All environment variables:",
        Object.keys(process.env).filter((key) => key.startsWith("REACT_APP"))
      );

      if (rttmPath) {
        // If it's a GitHub path, convert to raw URL
        if (rttmPath.startsWith("github/")) {
          dataUrl = `https://raw.githubusercontent.com/username/repo/main/${rttmPath}`;
        }
        // If it's a local file path (starts with /), it's already processed
        else if (rttmPath.startsWith("/")) {
          dataUrl = rttmPath;
        }
        // If it's a full URL, return as-is
        else if (rttmPath.startsWith("http")) {
          dataUrl = rttmPath;
        } else {
          dataUrl = rttmPath;
        }
      } else {
        // Fallback to default local file
        dataUrl = "/transcript_pretty.json";
      }

      console.log("Loading conversation data from:", dataUrl);
    }

    const response = await fetch(dataUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const speakerData = await response.json();
    console.log("Loaded speaker data:", speakerData);

    // Handle both old format (just original_rttm) and new format (original_rttm + modified_rttm)
    let originalRttmData, currentRttmData;

    if (speakerData.original_rttm && speakerData.modified_rttm) {
      // New format with both original and modified
      originalRttmData = speakerData.original_rttm;
      currentRttmData = speakerData.modified_rttm;
      console.log("Using new format with both original and modified RTTM");
    } else if (speakerData.original_rttm) {
      // Old format with just original_rttm
      originalRttmData = speakerData.original_rttm;
      currentRttmData = speakerData.original_rttm;
      console.log("Using old format with just original_rttm");
    } else {
      // Fallback for very old format
      originalRttmData = speakerData;
      currentRttmData = speakerData;
      console.log("Using fallback format");
    }

    // Transform current RTTM data to match expected conversation format
    const diarizationResult = {
      rttm: currentRttmData.map((segment) => ({
        start: segment.start,
        end: segment.end,
        speaker: segment.speaker === "speaker_0" ? "SPEAKER_00" : "SPEAKER_01",
      })),
      speaker_scores: {
        SPEAKER_00: 0.6, // Default scores since we don't have them
        SPEAKER_01: 0.4,
      },
      best_matched_speaker: "SPEAKER_00", // Default to first speaker
      n_speakers: 2,
    };

    console.log("Transformed diarization result:", diarizationResult);
    console.log("Original RTTM data:", originalRttmData);
    console.log("Current RTTM data:", currentRttmData);
    console.log("Finalized flag:", speakerData.finalized);

    // Store original RTTM data globally for saving later (always in SPEAKER_XX format)
    window.originalRttmData = originalRttmData.map((segment) => ({
      start: segment.start,
      end: segment.end,
      speaker: segment.speaker === "speaker_0" ? "SPEAKER_00" : "SPEAKER_01",
    }));

    const conversation = {
      id: conversation_id,
      audio_url: await getS3AudioUrl(conversation_id, "original"),
      diarization_result: diarizationResult,
      // No transcript data since we're working with speaker-only data
      raw_transcript: null,
      raw_segments: null,
      raw_detected_num_speakers: 2,
      raw_language: "en",
      filtered_transcript: null,
      filtered_segments: null,
      // Add metadata
      uuid: speakerData.metadata?.uuid || conversation_id,
      status: "completed",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      // Add finalized flag
      finalized: speakerData.finalized || false,
    };

    return conversation;
  } catch (error) {
    console.error("Error loading local speaker data:", error);
    throw error;
  }
};

export const loadSignature = async (signature_id) => {
  try {
    const response = await Api.get(`/signatures/${signature_id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error("Error loading signature:", error);
    throw error;
  }
};

export const getSignatureList = async () => {
  try {
    const response = await Api.get("/signatures");
    return response.data;
  } catch (error) {
    console.error("Error fetching signature list:", error);
    throw error;
  }
};

export const getUserSignature = async () => {
  try {
    // Use the list endpoint to get user's signatures
    const response = await Api.get("/signatures?limit=1");
    const signatures = response.data.signatures || response.data;

    // Return the first (and likely only) signature for the user
    if (Array.isArray(signatures) && signatures.length > 0) {
      return signatures[0];
    }

    return null; // No signature found
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No signatures found
    }
    console.error("Error fetching user signature:", error);
    throw error;
  }
};

export const updateConversation = async (conversationId, updates) => {
  try {
    // For basic conversation updates (like source), update the conversation directly
    const conversationUpdates = {};

    // Only include fields that exist in the new schema
    if (updates.source) conversationUpdates.source = updates.source;

    let response;
    if (Object.keys(conversationUpdates).length > 0) {
      response = await Api.put(
        `/conversations/${conversationId}`,
        conversationUpdates
      );
    } else {
      // If no direct conversation fields to update, just fetch the existing conversation
      response = await Api.get(`/conversations/${conversationId}`);
    }

    // If there are data updates (like analysis, transcript, etc.), store them as ConversationDetailItems
    if (updates.analysis) {
      await Api.post("/conversation_detail_items", {
        conversation_id: conversationId,
        type: "ANALYSIS",
        description: "Analysis results (updated)",
        data: updates.analysis,
      });
    }

    if (updates.raw_transcript_data) {
      await Api.post("/conversation_detail_items", {
        conversation_id: conversationId,
        type: "RAW_TRANSCRIPT",
        description: "Raw transcript data (updated)",
        data: updates.raw_transcript_data,
      });
    }

    if (updates.filtered_transcript_data) {
      await Api.post("/conversation_detail_items", {
        conversation_id: conversationId,
        type: "FILTERED_TRANSCRIPT",
        description: "Filtered transcript data (updated)",
        data: updates.filtered_transcript_data,
      });
    }

    return response.data;
  } catch (error) {
    console.error("Error updating conversation:", error);
    throw error;
  }
};

export const saveRttmToOutputFolder = async (
  diarizationData,
  finalized = false,
  baseName = null
) => {
  const outputFolder = process.env.REACT_APP_OUTPUT_FOLDER;
  console.log("Output folder from env:", outputFolder);
  console.log(
    "All React env vars:",
    Object.keys(process.env).filter((key) => key.startsWith("REACT_APP"))
  );

  try {
    // Convert diarization data to the same JSON format as the original
    let rttmData;

    // Handle different data structures
    if (diarizationData.rttm) {
      rttmData = diarizationData.rttm;
    } else if (Array.isArray(diarizationData)) {
      rttmData = diarizationData;
    } else {
      // Try to extract from any nested structure
      rttmData =
        diarizationData.diarization_result?.rttm ||
        diarizationData.updated_diarization?.rttm ||
        [];
    }

    console.log("RTTM data being saved:", rttmData);
    console.log("RTTM data type:", typeof rttmData);
    console.log("RTTM data isArray:", Array.isArray(rttmData));

    if (!Array.isArray(rttmData) || rttmData.length === 0) {
      console.error("Invalid RTTM data structure:", rttmData);
      throw new Error("No valid RTTM data found to save");
    }

    // Get original RTTM data (never changes)
    const originalRttm = window.originalRttmData || rttmData;

    // Transform data helper function
    const transformRttm = (data) => {
      if (!Array.isArray(data)) return [];
      return data.map((segment) => ({
        start: segment.start || 0,
        end: segment.end || 0,
        duration: (segment.end || 0) - (segment.start || 0),
        speaker: segment.speaker === "SPEAKER_00" ? "speaker_0" : "speaker_1",
      }));
    };

    const jsonContent = {
      original_rttm: transformRttm(originalRttm),
      modified_rttm: transformRttm(rttmData),
      finalized: finalized, // Use the passed finalized parameter
      metadata: {
        uuid: "saved-" + Date.now(),
        saved_at: new Date().toISOString(),
      },
    };

    if (outputFolder) {
      // Save directly to the specified output folder
      const response = await fetch("http://localhost:3001/save-file", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          outputPath: outputFolder,
          content: JSON.stringify(jsonContent, null, 2),
          filename:
            (baseName || process.env.REACT_APP_AUDIO_BASENAME || "audio") +
            ".json",
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save file: ${response.status}`);
      }

      const result = await response.json();

      // After saving, switch React to load from the output file
      try {
        const switchResponse = await fetch(
          "http://localhost:3001/switch-to-output",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              outputFilePath: result.filePath,
            }),
          }
        );

        if (switchResponse.ok) {
          console.log("Successfully switched to output file");
        }
      } catch (switchError) {
        console.warn("Failed to switch to output file:", switchError);
      }

      return { success: true, message: `File saved to ${result.filePath}` };
    } else {
      // Fallback: download the file if no output folder specified
      const blob = new Blob([JSON.stringify(jsonContent, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        (baseName || process.env.REACT_APP_AUDIO_BASENAME || "audio") + ".json";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true, message: "JSON file downloaded" };
    }
  } catch (error) {
    console.error("Error saving JSON file:", error);
    throw error;
  }
};

export const getConversationAnalysis = async (
  conversationId,
  skip = 0,
  limit = 100
) => {
  try {
    const response = await Api.get(
      `/conversations/${conversationId}/analysis?skip=${skip}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching conversation analysis:", error);
    throw error;
  }
};

// Admin Signature Management Functions
export const createSignatureForUser = async (userId, signatureData) => {
  try {
    const response = await Api.post(
      `/admin/signatures?user_id=${userId}`,
      signatureData
    );
    return response.data;
  } catch (error) {
    console.error("Error creating signature for user:", error);
    throw error;
  }
};

export const updateSignatureForUser = async (signatureId, signatureData) => {
  try {
    const response = await Api.put(
      `/admin/signatures/${signatureId}`,
      signatureData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating signature:", error);
    throw error;
  }
};

export const deleteSignature = async (signatureId) => {
  try {
    const response = await Api.delete(`/admin/signatures/${signatureId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting signature:", error);
    throw error;
  }
};

export const getAllSignaturesAdmin = async (skip = 0, limit = 100) => {
  try {
    const response = await Api.get(
      `/admin/signatures?skip=${skip}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching all signatures:", error);
    throw error;
  }
};
